import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Compass, Sparkles, Mail, Clock, CheckCircle2, Calendar, Wallet, Search, ArrowRight, CalendarSearch, CalendarDays, Library, Radio, CalendarX2, ShieldCheck, Zap } from "lucide-react";
import { api, apiError, formatDate, money, STATUS_META } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import EventCard from "@/components/EventCard";
import OrganizerRep from "@/components/OrganizerRep";
import { EmptyState } from "@/components/common";

const TONE = { amber: "text-primary", orange: "text-accent", emerald: "text-emerald-400", red: "text-red-400", sky: "text-sky-400" };

function Stat({ label, value, icon: Icon, tone = "amber", onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="text-left bg-card border border-border rounded-2xl p-4 sm:p-5 min-h-[92px] hover:border-primary/40 hover:shadow-[0_8px_30px_rgba(0,0,0,0.25)] transition-all active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold leading-tight">{label}</span>
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 shrink-0 ${TONE[tone]}`}><Icon className="h-4 w-4" /></span>
      </div>
      <div className={`font-mono text-2xl sm:text-3xl font-bold mt-2 ${TONE[tone]}`}>{value}</div>
    </button>
  );
}

const SHORTCUTS = [
  { to: "/buscar-eventos", label: "Buscar eventos", icon: CalendarSearch },
  { to: "/agenda", label: "Agenda", icon: CalendarDays },
  { to: "/repertorio", label: "Repertório", icon: Library },
  { to: "/plantao", label: "Plantão", icon: Radio },
];

function PlantaoCard() {
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/oncall/me").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const active = st?.active?.some((s) => s.status === "ativo");
  const go = async () => {
    if (!st?.oncall_enabled) return navigate("/plantao");
    setBusy(true);
    try { await api.post("/oncall", { mode: "now", duration_min: 240 }); toast.success("⚡ Plantão ativo! Você aparecerá nas chamadas Em cima da Hora."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <section data-testid="plantao-card" className={`rounded-2xl border p-4 flex items-center gap-4 ${active ? "border-[#FF4D00]/40 bg-[#FF4D00]/[0.06]" : "border-border bg-card"}`}>
      <span className={`flex h-11 w-11 items-center justify-center rounded-xl shrink-0 ${active ? "bg-[#FF4D00]/20 text-[#FF4D00]" : "bg-white/5 text-muted-foreground"}`}><Radio className="h-5 w-5" /></span>
      <div className="min-w-0 flex-1">
        <div className="font-head font-bold flex items-center gap-1.5">Modo Plantão {active && <span data-testid="plantao-active-dot" className="text-[10px] px-1.5 py-0.5 rounded bg-[#FF4D00]/20 text-[#FF4D00] border border-[#FF4D00]/30">ATIVO</span>}</div>
        <p className="text-xs text-muted-foreground">{active ? "Você está visível para chamadas de última hora." : "Fique disponível agora para receber chamadas Em cima da Hora."}</p>
      </div>
      {active ? (
        <Button data-testid="plantao-manage-btn" onClick={() => navigate("/plantao")} variant="outline" size="sm" className="rounded-xl bg-transparent border-white/10 shrink-0">Gerenciar</Button>
      ) : (
        <Button data-testid="plantao-now-btn" onClick={go} disabled={busy} size="sm" className="rounded-xl bg-[#FF4D00] hover:bg-[#E64500] text-white gap-1.5 shrink-0"><Zap className="h-4 w-4" />Disponível agora</Button>
      )}
    </section>
  );
}

export default function FreelancerDashboard({ d, reload, embedded }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const firstName = (user?.stage_name || "").split(" ")[0] || "";
  const f = d.freelancer || {};
  const next = f.next_event;

  const onSearch = (e) => {
    e.preventDefault();
    navigate(q.trim() ? `/buscar-eventos?q=${encodeURIComponent(q.trim())}` : "/buscar-eventos");
  };

  const confirmApp = async (appId) => {
    try { await api.post(`/applications/${appId}/confirm`); toast.success("Participação confirmada!"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const respondInvite = async (appId, accept) => {
    try { await api.post(`/applications/${appId}/invite-response`, { accept }); toast.success(accept ? "Convite aceito — presença confirmada!" : "Convite recusado."); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-7 fade-up" data-testid="freelancer-dashboard">
      {!embedded && (
      <section className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-card to-card p-5 sm:p-8">
        <div className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="min-w-0">
            {firstName && <p className="text-sm text-muted-foreground">Olá, <span className="text-foreground font-semibold">{firstName}</span> 👋</p>}
            <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 leading-tight">
              Vamos encontrar seu <span className="bg-gradient-to-r from-primary to-amber-400 bg-clip-text text-transparent">próximo palco?</span>
            </h1>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base">Oportunidades compatíveis com o seu perfil, prontas para você.</p>
          </div>
          <Button data-testid="freelancer-primary-cta-find-events" onClick={() => navigate("/buscar-eventos")} size="lg" className="gap-2 shrink-0 shadow-[0_0_20px_hsl(var(--primary)/0.35)]">
            <Compass className="h-5 w-5" /> Encontrar eventos
          </Button>
        </div>
        <form onSubmit={onSearch} className="relative mt-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <input data-testid="home-search-input" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar eventos e oportunidades"
            placeholder="Buscar eventos, oportunidades…"
            className="w-full bg-background/60 border border-border rounded-xl pl-12 pr-4 py-3 min-h-[48px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all" />
        </form>
      </section>
      )}

      <PlantaoCard />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <Stat testid="stat-card-new-opportunities" label="Novas oportunidades" value={f.new_opportunities || 0} icon={Sparkles} tone="amber" onClick={() => navigate("/eventos")} />
        <Stat testid="stat-card-pending-invites" label="Convites pendentes" value={f.pending_invites || 0} icon={Mail} tone="orange" onClick={() => document.querySelector('[data-testid="freelancer-invites-feed"]')?.scrollIntoView({ behavior: "smooth", block: "start" })} />
        <Stat testid="stat-card-applications-review" label="Candidaturas em análise" value={f.applications_in_review || 0} icon={Clock} tone="sky" onClick={() => navigate("/eventos?tab=applied")} />
        <Stat testid="stat-card-confirmed-gigs" label="Trabalhos confirmados" value={f.confirmed_gigs || 0} icon={CheckCircle2} tone="emerald" onClick={() => navigate("/agenda")} />
        <Stat testid="stat-card-next-event" label="Próximo evento" value={next ? formatDate(next.date) : "—"} icon={Calendar} tone="amber" onClick={() => next ? navigate(`/eventos/${next.id}`) : navigate("/agenda")} />
        <Stat testid="stat-card-receivables" label="Valores a receber" value={money(f.receivables || 0)} icon={Wallet} tone="emerald" onClick={() => navigate("/pagamentos")} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {SHORTCUTS.map((s) => (
          <button key={s.to} data-testid={`freelancer-shortcut-${s.label.toLowerCase().replace(/\s/g, "-")}`} onClick={() => navigate(s.to)}
            className="group flex flex-col items-center justify-center gap-2 rounded-2xl border border-border bg-card p-4 min-h-[96px] hover:border-primary/40 hover:bg-surface-hover transition-all active:scale-[0.98]">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors"><s.icon className="h-5 w-5" /></span>
            <span className="text-sm font-semibold text-center">{s.label}</span>
          </button>
        ))}
      </div>

      {(f.opportunities || []).length > 0 && (
        <section data-testid="freelancer-opportunities">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-head text-xl sm:text-2xl font-semibold tracking-tight">Oportunidades recomendadas</h2>
            <button onClick={() => navigate("/buscar-eventos")} className="text-sm text-accent flex items-center gap-1 hover:gap-2 transition-all">Ver todas <ArrowRight className="h-4 w-4" /></button>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{f.opportunities.map((ev, i) => <EventCard key={ev.id} ev={ev} index={i} onApplied={reload} />)}</div>
        </section>
      )}

      <section data-testid="freelancer-invites-feed">
        <h2 className="font-head text-xl sm:text-2xl font-semibold tracking-tight mb-4">Convites recebidos</h2>        {(d.invites || []).length === 0 ? (
          <EmptyState icon={Mail} description="Nenhum convite no momento. Candidate-se a oportunidades para aparecer no radar dos contratantes." />
        ) : (
          <div className="space-y-2">
            {d.invites.map((p) => (
              <div key={p.app_id} data-testid={`invite-${p.app_id}`} className="bg-card border border-info/40 rounded-2xl px-5 py-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <button className="min-w-0 text-left" onClick={() => navigate(`/eventos/${p.event.id}`)}>
                    <div className="font-medium truncate">{p.event.name}</div>
                    <div className="text-sm text-muted-foreground font-mono">{formatDate(p.event.date)} · {p.event.start_time}</div>
                    {p.event.payment_mode === "protected" && <span data-testid={`invite-protected-${p.app_id}`} className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/25"><ShieldCheck className="h-3 w-3" />Pagamento protegido</span>}
                  </button>
                  <div className="flex gap-2 shrink-0">
                    <Button data-testid={`btn-accept-invite-${p.app_id}`} onClick={() => respondInvite(p.app_id, true)} className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30">Aceitar</Button>
                    <Button data-testid={`btn-decline-invite-${p.app_id}`} variant="ghost" onClick={() => respondInvite(p.app_id, false)} className="text-red-400 hover:bg-red-500/15">Recusar</Button>
                  </div>
                </div>
                {p.organizer && (
                  <div className="pt-3 border-t border-border">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Quem está contratando</div>
                    <OrganizerRep org={p.organizer} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {(d.awaiting || []).length > 0 && (
        <section>
          <h2 className="font-head text-xl sm:text-2xl font-semibold tracking-tight mb-4">Aguardando sua confirmação</h2>
          <div className="space-y-2">
            {d.awaiting.map((p) => (
              <div key={p.app_id} data-testid={`awaiting-${p.app_id}`} className="flex items-center justify-between bg-card border border-warning/40 rounded-2xl px-5 py-4 gap-3">
                <button className="min-w-0 text-left" onClick={() => navigate(`/eventos/${p.event.id}`)}>
                  <div className="font-medium truncate">{p.event.name}</div>
                  <div className="text-sm text-muted-foreground font-mono">{formatDate(p.event.date)} · {p.event.start_time}</div>
                </button>
                <Button data-testid={`confirm-participation-${p.app_id}`} onClick={() => confirmApp(p.app_id)} className="shrink-0">Confirmar participação</Button>
              </div>
            ))}
          </div>
        </section>
      )}

      {(d.pending || []).length > 0 && (
        <section>
          <h2 className="font-head text-xl sm:text-2xl font-semibold tracking-tight mb-4">Candidaturas em análise</h2>
          <div className="space-y-2">
            {d.pending.map((p) => {
              const meta = STATUS_META[p.status];
              return (
                <button key={p.event.id} onClick={() => navigate(`/eventos/${p.event.id}`)} className="w-full flex items-center justify-between bg-card border border-border rounded-2xl px-5 py-4 text-left hover:border-primary/30 transition-colors">
                  <div><div className="font-medium">{p.event.name}</div><div className="text-sm text-muted-foreground font-mono">{formatDate(p.event.date)}</div></div>
                  <span className={`text-xs px-2.5 py-1 rounded-full border ${meta?.cls || "bg-muted text-muted-foreground border-border"}`}>{(meta?.label || p.status || "").split("—")[0].trim()}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-head text-xl sm:text-2xl font-semibold tracking-tight">Próximos trabalhos</h2>
          <button onClick={() => navigate("/agenda")} className="text-sm text-accent flex items-center gap-1 hover:gap-2 transition-all">Ver agenda <ArrowRight className="h-4 w-4" /></button>
        </div>
        {(d.upcoming || []).length === 0 ? (
          <EmptyState icon={CalendarX2} description="Nenhum trabalho confirmado a caminho." />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{d.upcoming.map((ev, i) => <EventCard key={ev.id} ev={ev} index={i} />)}</div>
        )}
      </section>
    </div>
  );
}
